<!-- Hero -->
<section appScrollReveal class="relative overflow-hidden bg-(--marine) text-white">
  <!-- Motif inspiré des textiles traditionnels camerounais (toghu/bamiléké — losanges à bordure
       dentelée, semés de petits accents), en camaïeu or sur le fond marine : abstraction géométrique
       originale (pas la reprise d'un tissu précis), discrète pour rester un fond derrière la photo
       et le texte plutôt que de leur faire concurrence. -->
  <svg class="pointer-events-none absolute inset-0 h-full w-full opacity-[0.13]" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
    <defs>
      <pattern id="opp-hero-pattern" width="80" height="80" patternUnits="userSpaceOnUse">
        <!-- Losange principal à bordure dentelée (dents en triangle, signature du toghu) -->
        <path d="M40 4 L48 12 L40 20 L32 12 Z M40 20 L48 28 L40 36 L32 28 Z M40 44 L48 52 L40 60 L32 52 Z M40 60 L48 68 L40 76 L32 68 Z"
          fill="none" stroke="#fcd116" stroke-width="1.1" />
        <path d="M4 40 L12 32 L20 40 L12 48 Z M20 40 L28 32 L36 40 L28 48 Z M44 40 L52 32 L60 40 L52 48 Z M60 40 L68 32 L76 40 L68 48 Z"
          fill="none" stroke="#fcd116" stroke-width="1.1" />
        <!-- Cœur du losange -->
        <path d="M40 26 L54 40 L40 54 L26 40 Z" fill="none" stroke="#fcd116" stroke-width="1.3" />
        <circle cx="40" cy="40" r="2.75" fill="#fcd116" />
        <!-- Dents d'angle : forment de petits losanges aux coutures du motif une fois répété -->
        <path d="M0 0 L9 0 L0 9 Z" fill="#fcd116" />
        <path d="M80 0 L71 0 L80 9 Z" fill="#fcd116" />
        <path d="M0 80 L9 80 L0 71 Z" fill="#fcd116" />
        <path d="M80 80 L71 80 L80 71 Z" fill="#fcd116" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#opp-hero-pattern)" />
  </svg>

  <div class="relative mx-auto grid max-w-300 grid-cols-1 items-end gap-8 px-6 pt-14 lg:grid-cols-2 lg:gap-4 lg:min-h-125 lg:pt-0">
    <div class="max-w-xl py-10 lg:py-26">
      <div class="mb-3 flex h-1 w-10 gap-1" aria-hidden="true">
        <span class="flex-1 bg-(--cmr-vert)"></span>
        <span class="flex-1 bg-(--cmr-rouge)"></span>
        <span class="flex-1 bg-(--cmr-jaune)"></span>
      </div>
      <h1 class="m-0 text-4xl font-extrabold leading-tight sm:text-5xl">
        {{ 'pages.opportunitiesList.heroTitle' | translate }}
      </h1>
      <p class="m-0 mt-5 max-w-lg text-[16px] leading-relaxed text-white/85">
        {{ 'pages.opportunitiesList.heroSubtitle' | translate }}
      </p>
    </div>

    <!-- Silhouettes détourées (fond transparent) : ancrées en bas du bandeau, pas de cadre/ombre —
         elles se fondent dans la scène plutôt que de flotter dans une carte photo. -->
    <div class="mx-auto w-full max-w-140 self-end lg:mx-0 lg:ml-auto lg:max-w-165 lg:translate-x-45">
      <img ngSrc="assets/images/pic/two-men-transparent-bg.png" alt="" width="1250" height="848" priority
        class="block h-auto w-full object-contain object-bottom" />
    </div>
  </div>
</section>

<!-- Filtres et grille d'opportunités (structure/logique inchangées) -->
<section class="bg-white">
  <div class="mx-auto max-w-300 px-6 py-9">
    <!-- Filtres par pilier + compteur -->
    <div class="mb-5 flex flex-wrap items-center gap-2" role="group" [attr.aria-label]="'pages.opportunitiesList.filterByCategory' | translate">
      @for (filter of filters; track filter.key) {
        <button type="button" (click)="setFilter(filter.key)"
          [attr.aria-pressed]="activeFilter() === filter.key"
          [class]="activeFilter() === filter.key
            ? 'border-(--pnud-bleu) bg-(--pnud-bleu) text-white'
            : 'border-(--border-strong) bg-white text-(--text-muted) hover:border-(--pnud-bleu) hover:text-(--pnud-bleu)'"
          class="cursor-pointer rounded-full border px-4 py-2 text-[13.5px] font-bold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--pnud-bleu)">
          {{ filter.labelKey | translate }}
        </button>
      }

      <span class="ml-auto text-[13px] text-(--text-muted)" aria-live="polite">
        <strong>{{ count() }}</strong> {{ 'pages.opportunitiesList.countSuffix' | translate }}
      </span>
    </div>

    <!-- Grille des opportunités -->
    @if (loading()) {
      <p class="py-8 text-center text-[13.5px] text-(--text-muted)" aria-live="polite">{{ 'pages.opportunitiesList.loading' | translate }}</p>
    } @else if (error()) {
      <p class="py-8 text-center text-[13.5px] text-(--state-danger)" role="alert">
        {{ 'pages.opportunitiesList.loadError' | translate }}
      </p>
    } @else {
      <div class="grid grid-cols-[repeat(auto-fill,minmax(310px,1fr))] gap-4">
        @for (opportunity of paginated(); track opportunity.id; let i = $index) {
          <div [appScrollReveal]="i">
            <app-opportunity-card
              [opportunity]="opportunity"
              [saved]="savedIds().has(opportunity.opportunityId)"
              (toggleSave)="toggleSave($event)"
              (open)="openOpportunity($event)" />
          </div>
        } @empty {
          <p class="col-span-full py-8 text-center text-[13.5px] text-(--text-muted)">
            {{ 'pages.opportunitiesList.noResults' | translate }}
          </p>
        }
      </div>

      @if (count() > pageSize) {
        <div class="mt-6 flex flex-wrap items-center justify-end gap-2">
          <button type="button"
            class="cursor-pointer rounded-full border border-(--border-strong) bg-white px-3.5 py-2 text-[13px] font-bold text-(--text-muted) disabled:cursor-not-allowed disabled:opacity-50"
            [disabled]="currentPage() === 0"
            (click)="setPage(currentPage() - 1)">
            {{ 'common.previous' | translate }}
          </button>

          <span class="text-[13px] text-(--text-muted)" aria-live="polite">
            {{ 'events.list.pageOf' | translate: { page: currentPage() + 1, total: totalPages() } }}
          </span>

          <button type="button"
            class="cursor-pointer rounded-full border border-(--border-strong) bg-white px-3.5 py-2 text-[13px] font-bold text-(--text-muted) disabled:cursor-not-allowed disabled:opacity-50"
            [disabled]="currentPage() >= totalPages() - 1"
            (click)="setPage(currentPage() + 1)">
            {{ 'common.next' | translate }}
          </button>
        </div>
      }
    }

    <!-- Bannière alerte -->
    <div
      class="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-md bg-(--surface-primary-tint) px-5 py-4 text-[13.5px] text-(--pnud-bleu)">
      <span>
        {{ 'pages.opportunitiesList.alertBannerText' | translate }}
      </span>
      <button type="button"
        class="box-border inline-flex h-8.5 cursor-pointer items-center rounded-sm border-0 bg-white px-3.5 text-[13.5px] font-bold text-(--pnud-bleu) shadow-(--shadow-card) transition hover:bg-(--gris-050) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--pnud-bleu)">
        {{ 'pages.opportunitiesList.createAlert' | translate }}
      </button>
    </div>
  </div>
</section>
